import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Animated Background */}
      <div className="auth-background">
        <div className="floating-dots">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="dot"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 10}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Login Card */}
      <div className="auth-card">
        {/* Logo */}
        <div className="auth-logo">
          <Logo size="xl" showText={false} />
        </div>

        {/* Header */}
        <div className="auth-header">
          <h1>Login</h1>
          <p>Please fill out the details to continue with us</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div style={{
              padding: '12px',
              background: '#FEE2E2',
              border: '1px solid #EF4444',
              borderRadius: '8px',
              color: '#991B1B',
              fontSize: '14px',
              marginBottom: '16px'
            }}>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <button type="submit" className="auth-submit-btn" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Log in'}
          </button>

          <p className="text-center text-sm text-[#515959] mt-4 mb-2">
            Field engineer?{' '}
            <Link to="/engineer/login" className="font-semibold text-[#2563eb] hover:underline">
              Sign in with email &amp; PIN
            </Link>
          </p>

          <div className="auth-link">
            <Link to="/forgot-password">Forgot Password?</Link>
          </div>
        </form>

        {/* Divider */}
        <div className="auth-divider">
          <span>Don't have an account?</span>
        </div>

        {/* Sign Up Link */}
        <Link to="/signup" className="auth-secondary-btn">
          Create Account
        </Link>
      </div>

      {/* Footer */}
      <div className="auth-footer">
        <p>
          You acknowledge that you read, and agree to our{' '}
          <a href="/terms">Terms and conditions</a>, and{' '}
          <a href="/privacy">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
